import { Add } from "@mui/icons-material";
import { Box, IconButton, Tooltip } from "@mui/material";
import { BaseModalComponent } from "../../../../common/BaseModalComponent";
import { VisitStepper } from "../../../../jobs2/JobVisits/job-visit-create/VisitStepper";
import type { Client, Job } from "../../../interfaces/job.interfaces";

type Props = {
  job: Job;
  onComplete?: () => void;
};

const ADD_CLIENT_LABEL = "No client selected";

export function AddVisitButton({ job, onComplete }: Props) {
  const activeDefaultWorkforce = (job.defaultWorkforce ?? []).filter((worker: any) => {
    const assignment = worker?.WorkforcesByJobs ?? worker?.WorkforcesByJobEvents;

    return assignment?.isDeleted !== true;
  });

  const hasDefaultWorkforce = activeDefaultWorkforce.length > 0;
  const hasClient = getClientDisplayName(job) !== ADD_CLIENT_LABEL;

  const iconButtonSx = {
    width: 28,
    height: 28,
    color: "primary.main",
    border: "1px solid rgba(25, 118, 210, 0.25)",
    "&:hover": {
      bgcolor: "rgba(25, 118, 210, 0.08)",
    },
  };

  if (!hasClient) {
    return (
      <Tooltip title="Add client first to create a visit.">
        <Box component="span" sx={{ display: "inline-flex" }}>
          <IconButton size="small" aria-label="Add new visit" disabled sx={iconButtonSx}>
            <Add sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Tooltip>
    );
  }

  return (
    <BaseModalComponent
      modalKey={`Job-Open-New-Event-${job.id}`}
      modalLabel="New Visit"
      elementTrigger={
        <Tooltip title="Add new visit">
          <IconButton size="small" aria-label="Add new visit" sx={iconButtonSx}>
            <Add sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      }
    >
      <VisitStepper
        jobId={job.id}
        closeModalKey={`Job-Open-New-Event-${job.id}`}
        defaultWorkforce={activeDefaultWorkforce}
        initialStep={hasDefaultWorkforce ? 1 : 0}
        onComplete={onComplete}
      />
    </BaseModalComponent>
  );
}

function resolveJobClient(job: Job): Client | null {
  const quoteClient = (job as Job & { quote?: { client?: Client | null } }).quote?.client;

  return job.client ?? quoteClient ?? job.events?.[0]?.client ?? job.events?.[0]?.job?.client ?? null;
}

function getClientDisplayName(job: Job) {
  const client = resolveJobClient(job);
  const name = [client?.firstName, client?.lastName].filter(Boolean).join(" ").trim();

  return name || ADD_CLIENT_LABEL;
}
